import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Lock, Headset } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Pill, fmtDateTime, inputCls } from "@/components/hub/Common";

const Bubble = ({ m, mineSide }) => (
  <div className={`flex ${mineSide ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-3 border ${mineSide ? "bg-[#2f7bff]/15 border-[#2f7bff]/35" : "bg-[#a746ff]/10 border-[#a746ff]/30"}`}>
      <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400">
        {m.from === "admin" && <Headset className="w-3 h-3 text-[#c79bff]" />}<b className="text-slate-200">{m.author}</b> · {fmtDateTime(m.at)}
      </div>
      <p className="text-sm text-slate-200 mt-1 whitespace-pre-line break-words">{m.text}</p>
    </div>
  </div>
);

export const TicketThread = ({ id, admin = false, onChange }) => {
  const { t } = useI18n();
  const [tk, setTk] = useState(null);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = useCallback(() => api.get(`/support/tickets/${id}`).then((r) => setTk(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [tk]);

  const send = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post(`/support/tickets/${id}/messages`, { message: text }); setTk(data); setText(""); onChange?.(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const close = async () => {
    try { await api.post(`/support/tickets/${id}/close`); await load(); onChange?.(); } catch (err) { toast.error(formatApiError(err)); }
  };
  if (!tk) return null;
  const closed = tk.status === "closed";
  return (
    <div className="cx-card rounded-2xl flex flex-col max-h-[70vh] min-h-[420px]" data-testid="ticket-thread">
      <div className="p-5 border-b border-white/5 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg text-white truncate" data-testid="ticket-thread-subject">{tk.subject}</h3>
          <div className="text-xs text-slate-500 mt-0.5 truncate">{admin ? `${tk.user_name} · ${tk.user_email} · ${tk.tenant_id}` : t(`sup.cat_${tk.category}`)}</div>
        </div>
        <Pill tone={tk.status} testid="ticket-thread-status">{t(`sup.st_${tk.status}`)}</Pill>
        {!closed && <button data-testid="ticket-close-btn" onClick={close} className="cx-ghost rounded-full px-3 py-1.5 text-xs flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" />{t("sup.close")}</button>}
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="ticket-messages">
        {tk.messages.map((m, i) => <Bubble key={i} m={m} mineSide={admin ? m.from === "admin" : m.from === "user"} />)}
        <div ref={end} />
      </div>
      {closed && !admin ? (
        <div className="p-4 border-t border-white/5 text-sm text-slate-500" data-testid="ticket-closed-note">{t("sup.closed_note")}</div>
      ) : (
        <form onSubmit={send} className="p-4 border-t border-white/5 flex gap-3">
          <textarea data-testid="ticket-reply-input" className={`${inputCls} min-h-[48px] max-h-40 resize-y`} placeholder={t("sup.reply_ph")} value={text} onChange={(e) => setText(e.target.value)} required />
          <button type="submit" data-testid="ticket-reply-btn" disabled={!text.trim()} className="cx-btn rounded-full px-5 flex items-center gap-2 text-sm font-semibold shrink-0"><Send className="w-4 h-4" /><span className="hidden sm:inline">{t("sup.reply")}</span></button>
        </form>
      )}
    </div>
  );
};
